import React, { useState } from 'react';
import { TrendingUp, TrendingDown } from 'lucide-react';
import GlobalThreatMap from '@/components/features/threat-feeds/GlobalThreatMap';
import { useGetGlobalStatsQuery } from '@/lib/services/dashboardApi';
import { useGetThreatOverviewQuery } from '@/lib/services/threatFeedsApi';

// Helper to ensure countries map correctly to TopoJSON names
const COUNTRY_ALIASES: Record<string, string> = {
    "united states": "United States of America",
    "usa": "United States of America",
    "uk": "United Kingdom",
    "russia": "Russian Federation",
};

const Dashboard: React.FC = () => {
    // Time Window State (24h, 7d, 30d)
    const [timeWindow, setTimeWindow] = useState<'24h' | '7d' | '30d'>('30d');

    // API Hooks
    const { data: globalStatsResponse, isLoading: isStatsLoading } = useGetGlobalStatsQuery({ window: timeWindow });
    const { data: overviewData, isLoading: isOverviewLoading } = useGetThreatOverviewQuery();

    // --- FIX 1: RESILIENT DATA EXTRACTION ---
    // Handles if your Axios baseApi wraps the response in a `.data` object
    const apiResponse = globalStatsResponse?.data || globalStatsResponse;
    const widgets = apiResponse?.widgets;

    const rawMapData = widgets?.global_threat_heat_map || [];
    const kpis = widgets?.kpis;
    const targetDistribution = widgets?.target_distribution || [];

    // Safely Map Aliasing for Map Component
    const mapData = rawMapData.map((geo: any) => {
        const idStr = String(geo?.id || '');
        return {
            id: COUNTRY_ALIASES[idStr.toLowerCase()] || idStr,
            value: geo?.value || 0
        };
    });

    // Header Status Variables
    const lastSyncString = overviewData?.meta?.last_sync_utc ? `${overviewData.meta.last_sync_utc} UTC` : 'AWAITING TELEMETRY...';
    const systemStatus = overviewData?.status ? overviewData.status.toUpperCase() : 'ELEVATED';
    const statusColorClass = systemStatus === 'SUCCESS' ? 'text-[#50A970]' : 'text-[#FF3333]';

    // Helper: Render individual KPI blocks with trend arrows
    const renderKPI = (title: string, data: any) => {
        if (isStatsLoading || !data) return (
            <div className="flex flex-col border-r border-[#1A1D24] last:border-0 pr-6 last:pr-0 animate-pulse">
                <div className="h-3 w-32 bg-[#1A1D24] rounded mb-3"></div>
                <div className="h-7 w-24 bg-[#1A1D24] rounded"></div>
            </div>
        );

        const isPositive = data.pct_delta > 0;

        return (
            <div className="flex flex-col border-r border-[#1A1D24] last:border-0 pr-6 last:pr-0">
                <div className="text-[11px] text-slate-500 font-mono mb-2 tracking-wide">
                    {title} ({data.window || '30 Days'})
                </div>
                <div className="flex items-end justify-between">
                    <div className="text-2xl font-bold text-white tracking-tight">
                        {data.value?.toLocaleString() || 0}
                    </div>
                    {data.pct_delta !== undefined && data.pct_delta !== 0 && (
                        <div className={`flex items-center text-[10px] font-bold tracking-wider ${isPositive ? 'text-[#50A970]' : 'text-[#FF3333]'}`}>
                            {isPositive ? <TrendingUp className="w-3 h-3 mr-1" /> : <TrendingDown className="w-3 h-3 mr-1" />}
                            {isPositive ? '+' : ''}{data.pct_delta}%
                        </div>
                    )}
                </div>
            </div>
        );
    };

    // Helper: Render the 6-segment dashed progress bars matching the Figma UI
    const renderSegmentedBar = (percent: number) => {
        const TOTAL_SEGMENTS = 6;
        const activeSegments = Math.ceil((percent / 100) * TOTAL_SEGMENTS);

        return (
            <div className="flex gap-1 w-full h-[6px] mt-2">
                {[...Array(TOTAL_SEGMENTS)].map((_, i) => (
                    <div
                        key={i}
                        className={`flex-1 rounded-sm transition-colors duration-500 ${i < activeSegments
                            ? 'bg-[#50A970] shadow-[0_0_8px_rgba(80,169,112,0.4)]'
                            : 'bg-[#1A1D24]'
                            }`}
                    ></div>
                ))}
            </div>
        );
    };

    return (
        <div className="flex flex-col gap-6 p-8 min-h-screen bg-black text-white font-mono relative overflow-x-hidden">

            {/* Header */}
            <div className="flex justify-between items-end mb-2">
                <div>
                    <h1 className="text-[22px] font-bold text-white tracking-wide">Global Stats</h1>
                    <p className={`text-[10px] mt-1 font-semibold uppercase tracking-widest ${statusColorClass}`}>
                        System_Status: {isOverviewLoading ? 'SYNCING...' : systemStatus}
                    </p>
                </div>
                <div className="flex items-center gap-2 text-[10px] text-slate-500 uppercase tracking-wider">
                    <span>Last_Sync: {isOverviewLoading ? 'Fetching...' : lastSyncString}</span>
                    <span className="w-2 h-2 rounded-full bg-[#50A970] shadow-[0_0_8px_rgba(80,169,112,0.8)] animate-pulse"></span>
                </div>
            </div>

            {/* Main Content Grid */}
            <div className="flex flex-col gap-5 relative z-10 mt-2 max-w-[1200px]">

                {/* 1. Global Threat Heatmap Section */}
                <div className="bg-[#0A0B0E] border border-[#1A1D24] rounded-xl p-6 shadow-lg flex flex-col relative shrink-0">
                    <div className="flex justify-between items-center mb-6 shrink-0 relative z-10">
                        <h2 className="text-[14px] font-bold tracking-wide text-white flex items-center gap-2">
                            Global Threat Heatmap
                        </h2>

                        {/* UI Updated Time Window Toggles */}
                        <div className="flex items-center gap-1.5 text-[10px] font-bold tracking-widest bg-[#12151C] border border-[#1A1D24] p-1 rounded-lg">
                            <button
                                onClick={() => setTimeWindow('24h')}
                                className={`px-3 py-1.5 rounded transition-all duration-200 ${timeWindow === '24h' ? 'bg-[#50A970]/10 text-[#50A970]' : 'text-slate-500 hover:text-slate-300'
                                    }`}
                            >
                                24 Hrs
                            </button>
                            <button
                                onClick={() => setTimeWindow('7d')}
                                className={`px-3 py-1.5 rounded transition-all duration-200 ${timeWindow === '7d' ? 'bg-[#50A970]/10 text-[#50A970]' : 'text-slate-500 hover:text-slate-300'
                                    }`}
                            >
                                7D
                            </button>
                            <button
                                onClick={() => setTimeWindow('30d')}
                                className={`px-3 py-1.5 rounded transition-all duration-200 ${timeWindow === '30d' ? 'bg-[#50A970]/10 text-[#50A970]' : 'text-slate-500 hover:text-slate-300'
                                    }`}
                            >
                                30D
                            </button>
                        </div>
                    </div>

                    {/* FIX 2: Explicit Height to prevent Canvas collapse */}
                    <div className="w-full h-[380px] relative rounded-lg overflow-hidden border border-[#1A1D24]/50">
                        {isStatsLoading ? (
                            <div className="absolute inset-0 flex items-center justify-center bg-black">
                                <div className="text-[#50A970] text-xs animate-pulse tracking-widest uppercase">
                                    Rendering Map Topology...
                                </div>
                            </div>
                        ) : mapData.length > 0 ? (
                            <GlobalThreatMap data={mapData} isLoading={false} />
                        ) : (
                            <div className="absolute inset-0 flex items-center justify-center bg-black text-slate-500 text-xs font-sans italic">
                                No geographic targeting data recorded for this window.
                            </div>
                        )}
                    </div>
                </div>

                {/* 2. KPI Horizontal Bar */}
                <div className="bg-[#0A0B0E] border border-[#1A1D24] rounded-xl p-6 shadow-lg shrink-0">
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                        {renderKPI('Total Leak Entries', kpis?.total_leak_entries)}
                        {renderKPI('Infected Devices', kpis?.infected_devices)}
                        {renderKPI('Stealer Log Entries', kpis?.stealer_log_entries)}
                        {renderKPI('Combo list Entries', kpis?.combo_list_entries)}
                    </div>
                </div>

                {/* 3. Target Distribution Progress Bars */}
                <div className="bg-[#0A0B0E] border border-[#1A1D24] rounded-xl p-6 shadow-lg shrink-0">
                    <h3 className="text-[14px] font-bold text-white mb-6 tracking-wide">Target Distribution</h3>

                    <div className="flex flex-col gap-5">
                        {isStatsLoading ? (
                            [...Array(6)].map((_, i) => (
                                <div key={i} className="animate-pulse">
                                    <div className="flex justify-between mb-2">
                                        <div className="h-3 w-32 bg-[#1A1D24] rounded"></div>
                                        <div className="h-3 w-16 bg-[#1A1D24] rounded"></div>
                                    </div>
                                    <div className="flex gap-1 w-full h-[6px] mt-2">
                                        {[...Array(6)].map((_, j) => <div key={j} className="flex-1 rounded-sm bg-[#1A1D24]"></div>)}
                                    </div>
                                </div>
                            ))
                        ) : targetDistribution.length > 0 ? (
                            targetDistribution.map((target: any, idx: number) => {
                                // Scale progress relative to the highest count in the array
                                const maxCnt = Math.max(...targetDistribution.map((d: any) => d.cnt), 1);
                                const percent = Math.max((target.cnt / maxCnt) * 100, 1);

                                return (
                                    <div key={idx} className="group">
                                        <div className="flex justify-between items-end text-[11px] font-mono mb-1.5">
                                            <span className="text-slate-300 group-hover:text-white transition-colors">{target.value}</span>
                                            <span className="text-slate-400 group-hover:text-slate-300 transition-colors">{target.cnt?.toLocaleString()}</span>
                                        </div>
                                        {/* Renders the segmented dashed bar matching the mockup */}
                                        {renderSegmentedBar(percent)}
                                    </div>
                                );
                            })
                        ) : (
                            <div className="text-slate-500 text-xs italic font-sans py-4 text-center border border-dashed border-[#1A1D24] rounded-lg">
                                No target distribution data available.
                            </div>
                        )}
                    </div>
                </div>

            </div>

            <style dangerouslySetInnerHTML={{
                __html: `
                .custom-scrollbar::-webkit-scrollbar { width: 4px; height: 4px; }
                .custom-scrollbar::-webkit-scrollbar-track { background: transparent; }
                .custom-scrollbar::-webkit-scrollbar-thumb { background: #1A1D24; border-radius: 4px; }
                .custom-scrollbar::-webkit-scrollbar-thumb:hover { background: #50A970; }
            `}} />
        </div>
    );
};

export default Dashboard;